import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  apiBasePath,
  subscribeApiFetchClientResponses,
} from "@/lib/api/client";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useStoreSwitch } from "@/shared/services/useStoreSwitch";
import { useStoreQuery } from "@/shared/services/useStoreQuery";
import { useOrderStatusCountsQuery } from "@/modules/orders/services/useOrderStatusCountsQuery";
import { useSellerTasks } from "@/shared/services/useSellerTasks";
import { useThemeStore } from "@/shared/theme/useTheme";
import type { SellerAuthView, SellerPermission } from "@/lib/api/types";
import { getSellerPath, getBuyerAbsoluteUrl } from "@/lib/routing/portal-urls";
import {
  classifySellerSessionFailure,
  getSellerLoginUrl,
} from "@/lib/auth/storeSlug";
import { PwaBootstrap } from "@/modules/pwa/components/PwaBootstrap";
import { InstallPrompt } from "@/modules/pwa/components/InstallPrompt";
import { FcmBootstrap } from "@/modules/notifications/components/FcmBootstrap";
import { FcmRegistrationProvider } from "@/modules/notifications/FcmRegistrationProvider";
import { NotificationPromptBanner } from "@/modules/notifications/components/NotificationPromptBanner";
import { NotificationBell } from "@/modules/notifications/components/NotificationBell";
import { BottomNav, useBottomNavTabs } from "./BottomNav";
import { PersonalInfoModal } from "@/modules/profile/components/PersonalInfoModal";
import { PasswordModal } from "@/modules/profile/components/PasswordModal";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { useQueryClient } from "@tanstack/react-query";
import {
  PullToRefreshHost,
  useDefaultPullToRefresh,
} from "@/shared/pull-to-refresh";
import {
  Sidebar,
  type SidebarGroup,
  type SidebarSubItem,
  DashboardSidebarIcon,
  OrdersSidebarIcon,
  TransactionsSidebarIcon,
  CustomersSidebarIcon,
  ProductsSidebarIcon,
  CategoriesSidebarIcon,
  CouponsSidebarIcon,
  ReviewsSidebarIcon,
  StoreEditorSidebarIcon,
  SettingsSidebarIcon,
  ShareSidebarIcon,
} from "./Sidebar";
import { NavbarSearch } from "@/shared/components/NavbarSearch";
import { ShareMenu } from "@/shared/components/ShareMenu";
import { ConfirmDialog } from "@a2z/ui";
import { PwaHead } from "@/modules/pwa/components/PwaHead";
import { PageChromeProvider, usePageChromeValues } from "./PageChromeContext";
import { AccountMenu } from "./AccountMenu";
import { useLogoutMutation } from "@/shared/services/useLogoutMutation";
import { deregisterFcmTokenFromBackend } from "@/lib/firebase/fcm";
import { locales } from "@/lib/i18n/config";

type AppShellProps = {
  locale: string;
  storeSlug: string;
  loginUrl?: string | null;
  children: React.ReactNode;
};

function isSellerApiPath(pathname: string): boolean {
  return pathname.startsWith(`${apiBasePath}/seller/`);
}

async function readApiErrorCode(response: Response): Promise<string | null> {
  try {
    const payload = (await response.clone().json()) as { code?: unknown };
    if (typeof payload?.code !== "string") {
      return null;
    }
    return payload.code;
  } catch {
    return null;
  }
}

export function AppShell({
  locale,
  storeSlug,
  loginUrl,
  children,
}: AppShellProps) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const t = useTranslations();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const scrollContainerRef = useRef<HTMLElement>(null);

  // App-wide pull-to-refresh fallback: any page that doesn't register its own
  // handler still refreshes by re-fetching its active data. `invalidateQueries`
  // with no filter marks every query stale and refetches the ones currently
  // mounted — i.e. exactly what the visible page is showing. Pages with more
  // specific needs (Orders, Dashboard, …) register via `usePullToRefresh` and
  // take precedence over this default.
  useDefaultPullToRefresh(() => queryClient.invalidateQueries());
  const sellerLoginPath = getSellerLoginUrl(locale);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () =>
      typeof window !== "undefined" &&
      localStorage.getItem("seller_sidebar_collapsed") === "1",
  );
  const toggleSidebar = () =>
    setSidebarCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== "undefined") {
        localStorage.setItem("seller_sidebar_collapsed", next ? "1" : "0");
      }
      return next;
    });
  const [isSessionExpiryDialogOpen, setIsSessionExpiryDialogOpen] =
    useState(false);
  // Which account-menu modal (if any) is open. Driven from the avatar dropdown
  // on desktop, and from the minimal /profile screen on mobile.
  const [accountModal, setAccountModal] = useState<
    "personal-info" | "password" | null
  >(null);
  // Store share sheet — opened from the "Share store" nav entry on both the
  // desktop sidebar and the mobile menu.
  const [isShareOpen, setIsShareOpen] = useState(false);
  const didRedirectToLoginRef = useRef(false);
  const didOpenExpiryPromptRef = useRef(false);
  const { data: sessionData } = useSessionQuery({ storeSlug });
  const { data: storeData } = useStoreQuery({ storeSlug });
  const { isSwitching: isSwitchingStore, switchTo: switchStore } =
    useStoreSwitch(locale, storeSlug);
  const { data: orderStatusCounts } = useOrderStatusCountsQuery({
    storeSlug,
    refetchInterval: 2 * 60 * 1000,
  });
  const sellerTasks = useSellerTasks({ storeSlug, locale });
  const resolvedDark = useThemeStore((s) => s.resolvedDark);
  const setThemePref = useThemeStore((s) => s.setPref);

  const storeIdentity = useMemo<Pick<
    SellerAuthView,
    "storeName" | "storeShort"
  > | null>(
    () =>
      sessionData
        ? {
            storeName: sessionData.seller.storeName,
            storeShort: sessionData.seller.storeShort,
          }
        : null,
    [sessionData],
  );

  // Drive the browser tab title from the store name: "<Store> - Trioty".
  useEffect(() => {
    const name = storeIdentity?.storeName?.trim();
    document.title = name ? `${name} - Trioty` : "Trioty";
  }, [storeIdentity]);

  const overview = sessionData?.overview ?? null;

  // The member's resolved permissions (from /seller/auth/me). Used to hide nav
  // entries a role can't use — a Support member shouldn't see Products/Coupons
  // management, etc. This mirrors the backend @RequirePermission gates; the UI
  // hide is convenience, the API is the real enforcement. Owner has every
  // permission so sees everything.
  const grantedPermissions = useMemo(
    () => new Set(sessionData?.seller.permissions ?? []),
    [sessionData?.seller.permissions],
  );
  // Missing session (still loading) → treat as "allowed" so nothing flickers
  // out during load; once the session resolves the real set applies.
  const can = (permission: SellerPermission): boolean =>
    grantedPermissions.size === 0 || grantedPermissions.has(permission);

  // Derived order state for sidebar
  const actionableOrderCount =
    (orderStatusCounts?.counts.pending_payment_verification ?? 0) +
    (orderStatusCounts?.counts.paid_less_than_required ?? 0);

  const dashboardHref = getSellerPath(locale, storeSlug, "/dashboard");
  const storeSettingsHref = getSellerPath(locale, storeSlug, "/settings");
  const storeEditorHref = getSellerPath(locale, storeSlug, "/store/editor");
  const storeEditorSimpleHref = getSellerPath(
    locale,
    storeSlug,
    "/store/editor/simple",
  );
  const categoriesHref = getSellerPath(locale, storeSlug, "/categories");
  const productsHref = getSellerPath(locale, storeSlug, "/products");
  const ordersHref = getSellerPath(locale, storeSlug, "/orders");
  const transactionsHref = getSellerPath(locale, storeSlug, "/transactions");
  const customersHref = getSellerPath(locale, storeSlug, "/customers");
  const reviewsHref = getSellerPath(locale, storeSlug, "/reviews");
  const couponsHref = getSellerPath(locale, storeSlug, "/coupons");
  const discountsHref = getSellerPath(locale, storeSlug, "/discounts");
  const accountHref = getSellerPath(locale, storeSlug, "/account");
  // Storefront link shared from the nav's "Share store" entry. Computed here
  // (same helper the dashboard used) so the nav owns its own data instead of
  // threading a URL down through unrelated props.
  const storeUrl = getBuyerAbsoluteUrl(locale, storeSlug);
  const isDashboardActive = pathname?.startsWith(dashboardHref);
  const isStoreSettingsActive = pathname?.startsWith(storeSettingsHref);
  const isCategoriesActive = pathname?.startsWith(categoriesHref);
  const isProductsActive = pathname?.startsWith(productsHref);
  const isOrdersActive = pathname?.startsWith(ordersHref);
  const isTransactionsActive = pathname?.startsWith(transactionsHref) ?? false;
  const isCustomersActive = pathname?.startsWith(customersHref) ?? false;
  const isReviewsActive = pathname?.startsWith(reviewsHref) ?? false;
  const isCouponsActive = pathname?.startsWith(couponsHref) ?? false;
  const isDiscountsActive = pathname?.startsWith(discountsHref) ?? false;
  useEffect(() => {
    return subscribeApiFetchClientResponses(
      async ({ requestUrl, response }) => {
        if (
          !isSellerApiPath(requestUrl.pathname) ||
          didRedirectToLoginRef.current
        ) {
          return;
        }

        const errorCode = await readApiErrorCode(response);
        const failureKind = classifySellerSessionFailure(
          response.status,
          errorCode,
        );
        if (!failureKind) {
          return;
        }

        if (failureKind === "expired") {
          if (!didOpenExpiryPromptRef.current) {
            didOpenExpiryPromptRef.current = true;
            setIsSessionExpiryDialogOpen(true);
          }
          return;
        }

        didRedirectToLoginRef.current = true;
        setIsSessionExpiryDialogOpen(false);
        navigate(sellerLoginPath, { replace: true });
      },
    );
  }, [navigate, sellerLoginPath]);

  // ─── Sidebar nav model ──────────────────────────────────────────────────────
  // Order + grouping per roundtable verdict (balanced 3/3/3/3):
  // .roundtable/roundtable-2026-06-03-sidebar-order-brief.md
  // The simple editor lives under the store-editor path prefix, so match it
  // first and exclude it from the base editor's active state — otherwise both
  // items would light up when on `/store/editor/simple`.
  const isStoreEditorSimpleActive =
    pathname?.startsWith(storeEditorSimpleHref) ?? false;
  const isStoreEditorActive =
    (pathname?.startsWith(storeEditorHref) ?? false) &&
    !isStoreEditorSimpleActive;
  // Store settings → section submenu (branding is the default / param-less URL).
  const onSettings = isStoreSettingsActive ?? false;
  const currentSection =
    new URLSearchParams(search).get("section") ?? "branding";
  // Each settings section maps to the permission that lets a member do anything
  // there. A Support member (STORE_VIEW only) sees no settings sections and the
  // whole Store-settings entry drops out below.
  const settingsSubItems: SidebarSubItem[] = (
    [
      {
        href: storeSettingsHref,
        label: t(keys.seller.storeSettings.sectionBrandingLabel),
        isActive: onSettings && currentSection === "branding",
        permission: "STORE_EDIT",
      },
      {
        href: `${storeSettingsHref}?section=payment`,
        label: t(keys.seller.storeSettings.sectionPaymentLabel),
        isActive: onSettings && currentSection === "payment",
        permission: "PAYMENT_VIEW",
      },
      {
        href: `${storeSettingsHref}?section=shipping`,
        label: t(keys.seller.storeSettings.sectionShippingLabel),
        isActive: onSettings && currentSection === "shipping",
        permission: "SHIPPING_VIEW",
      },
      {
        href: `${storeSettingsHref}?section=inventory`,
        label: t(keys.seller.storeSettings.sectionInventoryLabel),
        isActive: onSettings && currentSection === "inventory",
        permission: "STORE_EDIT",
      },
      {
        href: `${storeSettingsHref}?section=policies`,
        label: t(keys.seller.storeSettings.sectionPoliciesLabel),
        isActive: onSettings && currentSection === "policies",
        permission: "STORE_EDIT",
      },
      {
        href: `${storeSettingsHref}?section=team`,
        label: t(keys.seller.storeSettings.sectionTeamLabel),
        isActive: onSettings && currentSection === "team",
        permission: "MEMBER_VIEW",
      },
      {
        href: `${storeSettingsHref}?section=domain`,
        label: t(keys.seller.storeSettings.sectionDomainLabel),
        isActive: onSettings && currentSection === "domain",
        permission: "STORE_EDIT",
      },
    ] satisfies (SidebarSubItem & { permission: SellerPermission })[]
  )
    .filter((sub) => can(sub.permission))
    .map(({ permission: _permission, ...sub }) => sub);

  // Promotions → Coupons (code) + Discounts (automatic). Both share the coupon
  // endpoint and the same COUPON_VIEW gate.
  const promotionsSubItems: SidebarSubItem[] = (
    [
      {
        href: couponsHref,
        label: t(keys.coupons.navLabel),
        isActive: isCouponsActive,
        permission: "COUPON_VIEW",
      },
      {
        href: discountsHref,
        label: t(keys.discounts.navLabel),
        isActive: isDiscountsActive,
        permission: "COUPON_VIEW",
      },
    ] satisfies (SidebarSubItem & { permission: SellerPermission })[]
  )
    .filter((sub) => can(sub.permission))
    .map(({ permission: _permission, ...sub }) => sub);

  // Each nav entry carries the VIEW permission that makes it usable. Entries
  // the member lacks are dropped, and a group with no remaining entries is
  // dropped whole. `null` = always shown (Dashboard is the landing page every
  // member can reach). Store settings shows if ANY of its sections survived.
  const rawGroups: (Omit<SidebarGroup, "items"> & {
    items: (SidebarGroup["items"][number] & {
      permission: SellerPermission | null;
    })[];
  })[] = [
    {
      header: t(keys.seller.navGroupStore),
      items: [
        {
          href: dashboardHref,
          label: t(keys.seller.dashboardTitle),
          icon: <DashboardSidebarIcon />,
          isActive: isDashboardActive ?? false,
          permission: null,
        },
        {
          href: ordersHref,
          label: t(keys.orders.title),
          icon: <OrdersSidebarIcon />,
          isActive: isOrdersActive ?? false,
          count: actionableOrderCount > 0 ? actionableOrderCount : undefined,
          permission: "ORDER_VIEW",
        },
        {
          href: transactionsHref,
          label: t(keys.transactions.navLabel),
          icon: <TransactionsSidebarIcon />,
          isActive: isTransactionsActive,
          // Store-wide gateway payment data — gated on the same permission as
          // the payment settings section.
          permission: "PAYMENT_VIEW",
        },
        {
          href: customersHref,
          label: t(keys.customers.navLabel),
          icon: <CustomersSidebarIcon />,
          isActive: isCustomersActive,
          // Customers are surfaced from orders; support reps (ORDER_VIEW) need them.
          permission: "ORDER_VIEW",
        },
        {
          href: productsHref,
          label: t(keys.seller.productsTitle),
          icon: <ProductsSidebarIcon />,
          isActive: isProductsActive ?? false,
          showHint: overview ? !overview.hasProducts : false,
          permission: "PRODUCT_VIEW",
        },
      ],
    },
    {
      header: t(keys.seller.navGroupCatalog),
      items: [
        {
          href: categoriesHref,
          label: t(keys.seller.categoriesTitle),
          icon: <CategoriesSidebarIcon />,
          isActive: isCategoriesActive ?? false,
          showHint: overview ? !overview.hasCategories : false,
          permission: "CATEGORY_VIEW",
        },
        {
          href: couponsHref,
          label: t(keys.promotions.navLabel),
          icon: <CouponsSidebarIcon />,
          isActive: isCouponsActive || isDiscountsActive,
          subItems: promotionsSubItems,
          permission: "COUPON_VIEW",
        },
        {
          href: reviewsHref,
          label: t(keys.reviews.navLabel),
          icon: <ReviewsSidebarIcon />,
          isActive: isReviewsActive,
          permission: "REVIEW_VIEW",
        },
      ],
    },
    {
      header: t(keys.seller.navGroupChannels),
      items: [
        {
          href: storeEditorHref,
          label: t(keys.seller.storeEditorTitle),
          icon: <StoreEditorSidebarIcon />,
          isActive: isStoreEditorActive,
          permission: "STORE_EDIT",
        },
        {
          href: storeEditorSimpleHref,
          label: t(keys.seller.storeEditorSimpleTitle),
          icon: <StoreEditorSidebarIcon />,
          isActive: isStoreEditorSimpleActive,
          permission: "STORE_EDIT",
        },
        {
          href: storeSettingsHref,
          label: t(keys.seller.storeSettingsTitle),
          icon: <SettingsSidebarIcon />,
          isActive: onSettings,
          subItems: settingsSubItems,
          // Show the settings entry if the member can reach at least one section.
          permission: settingsSubItems.length > 0 ? "STORE_VIEW" : "STORE_EDIT",
        },
        {
          // An ACTION, not a destination: no `href`, so Sidebar renders it as a
          // <button> that opens the share sheet. Never "active" — no URL.
          onClick: () => setIsShareOpen(true),
          label: t(keys.share.storeTitle),
          icon: <ShareSidebarIcon />,
          isActive: false,
          // Sharing the public storefront link needs no edit rights — anyone
          // who can see the store can hand out its URL.
          permission: "STORE_VIEW",
        },
      ],
    },
  ];

  const sidebarGroups: SidebarGroup[] = rawGroups
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) => item.permission === null || can(item.permission))
        .map(({ permission: _permission, ...item }) => item),
    }))
    .filter((group) => group.items.length > 0);

  const activeNavItem =
    sidebarGroups
      .flatMap((group) => group.items)
      .find((item) => item.isActive) ?? null;

  const bottomNavTabs = useBottomNavTabs({
    dashboardHref,
    ordersHref,
    customersHref,
    transactionsHref,
    productsHref,
    categoriesHref,
    couponsHref,
    discountsHref,
    reviewsHref,
    storeEditorHref,
    // Settings → the merged hub (store settings + notifications, theme,
    // language, logout, store switch), not just the store-settings page.
    settingsHref: accountHref,
    isDashboardActive: isDashboardActive ?? false,
    isOrdersActive: isOrdersActive ?? false,
    isCustomersActive,
    isTransactionsActive,
    isProductsActive: isProductsActive ?? false,
    isCategoriesActive: isCategoriesActive ?? false,
    isCouponsActive,
    isDiscountsActive,
    isReviewsActive,
    isStoreEditorActive,
    isSettingsActive:
      (pathname?.startsWith(accountHref) ?? false) || onSettings,
    productsShowHint: overview ? !overview.hasProducts : false,
    categoriesShowHint: overview ? !overview.hasCategories : false,
    // Payment setup lives under Settings.
    settingsShowHint: overview ? !overview.hasActivePaymentOptions : false,
  });

  const visibleBottomNavTabs = can("PAYMENT_VIEW")
    ? bottomNavTabs
    : bottomNavTabs.filter((tab) => tab.key !== "transactions");

  const handleSessionExpiryConfirm = () => {
    if (didRedirectToLoginRef.current) {
      return;
    }
    didRedirectToLoginRef.current = true;
    setIsSessionExpiryDialogOpen(false);
    navigate(sellerLoginPath, { replace: true });
  };

  const avatarInitial = (storeIdentity?.storeName ?? storeSlug ?? "?")
    .trim()
    .charAt(0)
    .toUpperCase();

  const { mutateAsync: sellerLogout } = useLogoutMutation();
  const handleLocaleChange = (next: "en" | "ar") => {
    if (next === locale) return;
    const path = pathname ?? "/";
    const localePattern = new RegExp(`^/(${locales.join("|")})(/|$)`);
    const nextPath = localePattern.test(path)
      ? path.replace(localePattern, `/${next}$2`)
      : `/${next}${path.startsWith("/") ? "" : "/"}${path}`;
    navigate(nextPath, { replace: true });
  };
  const handleLogout = async () => {
    await deregisterFcmTokenFromBackend().catch(() => {});
    await sellerLogout({});
    navigate(loginUrl ?? sellerLoginPath, { replace: true });
  };

  return (
    <PageChromeProvider>
      <FcmRegistrationProvider
        sellerId={sessionData?.seller?.id}
        storeId={sessionData?.seller?.storeId}
      >
        <div className="flex h-dvh w-full flex-col overflow-hidden bg-[color:var(--canvas)] p-0">
          <PwaHead />
          <PwaBootstrap />
          <FcmBootstrap
            storeId={sessionData?.seller?.storeId}
            storeSlug={storeSlug}
            firebaseToken={sessionData?.firebaseToken}
          />
          <PullToRefreshHost scrollRef={scrollContainerRef} />

          {/* Body row: sidebar (desktop) + content column */}
          <div className="flex min-h-0 flex-1 overflow-hidden">
            {!isMobile ? (
              <Sidebar
                dashboardHref={dashboardHref}
                settingsHref={storeSettingsHref}
                groups={sidebarGroups}
                hidden={sidebarCollapsed}
                onHide={toggleSidebar}
                identity={{
                  storeName: storeIdentity?.storeName ?? storeSlug,
                  storeShort: storeIdentity?.storeShort ?? null,
                  initial: avatarInitial,
                  logoUrl: storeData?.logoUrl ?? null,
                }}
                availableStores={sessionData?.availableStores ?? []}
                activeStoreSlug={storeSlug}
                onSwitchStore={(slug) => void switchStore(slug)}
                isSwitchingStore={isSwitchingStore}
                tasks={sellerTasks.tasks}
                tasksTitle={sellerTasks.title}
                accountSlot={
                  <AccountMenu
                    storeName={storeIdentity?.storeName ?? storeSlug}
                    storeShort={storeIdentity?.storeShort ?? null}
                    storeCode={storeIdentity?.storeShort ?? storeSlug}
                    sellerName={sessionData?.seller?.name ?? storeSlug}
                    sellerEmail={sessionData?.seller?.email ?? ""}
                    initial={avatarInitial}
                    locale={locale}
                    onLocaleChange={handleLocaleChange}
                    isDark={resolvedDark}
                    onThemeChange={setThemePref}
                    onPersonalInfo={() => setAccountModal("personal-info")}
                    onPassword={() => setAccountModal("password")}
                    onLogout={() => void handleLogout()}
                    labels={{
                      storeMenu: t(keys.seller.accountTitle),
                      live: t(keys.seller.storeLiveLabel),
                      copyCode: t(keys.seller.copyStoreLink),
                      codeCopied: t(keys.seller.linkCopiedTooltip),
                      language: t(keys.common.language),
                      theme: t(keys.common.theme),
                      themeLight: t(keys.common.themeLight),
                      themeDark: t(keys.common.themeDark),
                      themeSystem: t(keys.common.themeSystem),
                      personalInfo: t(keys.seller.profileSettings.personalInfo),
                      password: t(keys.seller.profileSettings.password),
                      logout: t(keys.common.logout),
                    }}
                  />
                }
              />
            ) : null}

            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[color:var(--canvas)]">
              {/* Desktop: dark chrome top bar — collapse toggle + breadcrumb + tools.
                Mobile: no global header; each screen renders its own contextual bar. */}
              {!isMobile ? (
                <div className="shrink-0 overflow-hidden bg-[color:var(--card)]">
                  <header className="flex h-16 shrink-0 items-center gap-3 border-b border-[color:var(--line)] bg-[color:var(--card)] px-6 text-[color:var(--text-primary)]">
                    {sidebarCollapsed ? (
                      <button
                        type="button"
                        onClick={toggleSidebar}
                        aria-label={t(keys.seller.navExpand)}
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--line)] bg-[color:var(--card)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover)] hover:text-[color:var(--text-primary)]"
                      >
                        <PanelIcon />
                      </button>
                    ) : null}
                    <TopBarLabel
                      fallbackIcon={
                        activeNavItem?.icon ?? <DashboardSidebarIcon />
                      }
                      fallbackLabel={
                        activeNavItem?.label ?? t(keys.seller.navControlPanel)
                      }
                    />
                    <div className="ms-auto flex items-center gap-2">
                      <NavbarSearch locale={locale} storeSlug={storeSlug} />
                      <NotificationBell
                        locale={locale}
                        storeSlug={storeSlug}
                        triggerClassName="h-[38px] w-[38px] rounded-[11px] border border-[color:var(--line)] bg-[color:var(--card)] text-[color:var(--text-secondary)] hover:bg-[color:var(--hover)] hover:text-[color:var(--text-primary)]"
                      />
                    </div>
                  </header>
                </div>
              ) : null}

              <main
                ref={scrollContainerRef}
                className="no-scrollbar flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain"
                data-scroll-container="portal"
              >
                <div className="w-full">
                  <HeroRegion />
                  <NotificationPromptBanner />
                  <InstallPrompt />
                  {children}
                  {/* Clearance for the floating mobile nav pill (fixed, ~76px + safe area). */}
                  <div className="h-28 lg:h-6" aria-hidden="true" />
                </div>
              </main>
            </div>
          </div>

          {isMobile ? (
            <BottomNav
              tabs={visibleBottomNavTabs}
              identity={{
                storeName: storeIdentity?.storeName ?? storeSlug,
                initial: avatarInitial,
                logoUrl: storeData?.logoUrl ?? null,
                href: accountHref,
              }}
              locale={locale}
              storeSlug={storeSlug}
              onShareStore={() => setIsShareOpen(true)}
            />
          ) : null}

          {/* Store share sheet — the single share implementation (ShareMenu),
              driven in controlled mode from the nav entry on both surfaces. */}
          <ShareMenu
            url={storeUrl}
            messageKey={keys.share.storeMessage}
            messageValues={{ store: storeIdentity?.storeName ?? storeSlug }}
            title={t(keys.share.storeTitle)}
            triggerAriaLabel={t(keys.share.storeAria)}
            open={isShareOpen}
            onClose={() => setIsShareOpen(false)}
          />


          <ConfirmDialog
            isOpen={isSessionExpiryDialogOpen}
            title={t(keys.seller.sessionExpiredTitle)}
            body={t(keys.seller.sessionExpiredBody)}
            confirmLabel={t(keys.common.login)}
            showCancelButton={false}
            onConfirm={handleSessionExpiryConfirm}
          />

          <PersonalInfoModal
            isOpen={accountModal === "personal-info"}
            onClose={() => setAccountModal(null)}
            storeSlug={storeSlug}
            seller={sessionData?.seller ?? null}
          />
          <PasswordModal
            isOpen={accountModal === "password"}
            onClose={() => setAccountModal(null)}
            storeSlug={storeSlug}
          />
        </div>
      </FcmRegistrationProvider>
    </PageChromeProvider>
  );
}

// Light canvas page-header at the top of the content area (DESIGN_CONTEXT §8).
// Desktop top-bar label. Renders the page-specific breadcrumb when a page has
// set one (e.g. "Products › {name}" on the edit page), otherwise the active nav
// item's icon + label. Lives in its own component so it can read the chrome
// value context from inside the provider.
function TopBarLabel({
  fallbackIcon,
  fallbackLabel,
}: {
  fallbackIcon: React.ReactNode;
  fallbackLabel: React.ReactNode;
}) {
  const { breadcrumb } = usePageChromeValues();
  if (breadcrumb) {
    return (
      <div className="flex min-w-0 items-center text-[15px] font-semibold text-[color:var(--text-primary)]">
        {breadcrumb}
      </div>
    );
  }
  return (
    <div className="flex min-w-0 items-center gap-2 text-[15px] font-semibold text-[color:var(--text-primary)]">
      <span
        aria-hidden="true"
        className="inline-flex shrink-0 [&_svg]:h-[19px] [&_svg]:w-[19px]"
      >
        {fallbackIcon}
      </span>
      <span className="truncate">{fallbackLabel}</span>
    </div>
  );
}

function HeroRegion() {
  const { hero, heroAux } = usePageChromeValues();
  if (!hero && !heroAux) return null;
  return (
    <div className="w-full px-4 pt-4 sm:px-5 sm:pt-5 lg:px-7">
      {hero}
      {heroAux ? <div className="mt-4">{heroAux}</div> : null}
    </div>
  );
}

// ─── Top-bar icons ────────────────────────────────────────────────────────────

function PanelIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <line x1="9" y1="4" x2="9" y2="20" />
    </svg>
  );
}
